import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Disc3,
  Music2,
  Mail,
  HandHeart,
  Plus,
  Trash2,
  Save,
  RefreshCw,
  ArrowLeft,
  X,
  Lock,
  LogOut,
  Calendar,
} from "lucide-react";
import { extractEmbedSrc } from "../utils/embed";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AUTH_KEY = "tekreck_admin_ok_v1";

const TABS = [
  { id: "albums", label: "Albums", icon: Disc3 },
  { id: "recent", label: "Recent Tracks", icon: Music2 },
  { id: "bookings", label: "Bookings", icon: Calendar },
  { id: "collabs", label: "Collabs", icon: Mail },
  { id: "prayers", label: "Prayer", icon: HandHeart },
];

export default function AdminPage() {
  const [authed, setAuthed] = useState(() => sessionStorage.getItem(AUTH_KEY) === "1");
  const [tab, setTab] = useState("albums");

  if (!authed) return <PasswordGate onAuth={() => setAuthed(true)} />;

  const signOut = () => {
    sessionStorage.removeItem(AUTH_KEY);
    setAuthed(false);
  };

  return (
    <div data-testid="admin-page" className="min-h-screen bg-[#050505] text-white">
      <header className="sticky top-0 z-40 bg-black/80 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-[1400px] mx-auto flex items-center justify-between px-6 md:px-10 h-16">
          <div className="flex items-center gap-3">
            <a
              href="/"
              data-testid="admin-back-home"
              className="text-white/60 hover:text-[#D4AF37]"
              title="Back to site"
            >
              <ArrowLeft size={18} />
            </a>
            <span className="h-2 w-2 rounded-full bg-[#D4AF37]" />
            <span className="font-display font-black tracking-[0.25em] text-sm">
              TEKRECK · ADMIN
            </span>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-xs text-white/40 uppercase tracking-[0.25em] hidden sm:block">
              /ygtoadmin155
            </div>
            <button
              data-testid="admin-signout"
              onClick={signOut}
              className="text-white/60 hover:text-[#D4AF37] inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em]"
              title="Sign out"
            >
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 md:px-10 pt-10 pb-24">
        <div className="flex flex-wrap gap-2 mb-10 border-b border-white/10 pb-3">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              data-testid={`admin-tab-${id}`}
              onClick={() => setTab(id)}
              className={`inline-flex items-center gap-2 h-10 px-5 text-xs uppercase tracking-[0.2em] border transition-all ${
                tab === id
                  ? "bg-[#D4AF37] text-black border-[#D4AF37]"
                  : "border-white/15 text-white/70 hover:text-[#D4AF37] hover:border-[#D4AF37]/60"
              }`}
            >
              <Icon size={14} />
              {label}
            </button>
          ))}
        </div>

        {tab === "albums" && <AlbumsTab />}
        {tab === "recent" && <RecentTab />}
        {tab === "bookings" && <ReadOnlyList endpoint="booking" title="Booking Inquiries" testIdPrefix="booking" kind="booking" />}
        {tab === "collabs" && <ReadOnlyList endpoint="collab" title="Collab Requests" testIdPrefix="collab" kind="collab" />}
        {tab === "prayers" && <ReadOnlyList endpoint="prayer" title="Prayer Requests" testIdPrefix="prayer" kind="prayer" />}
      </div>
    </div>
  );
}

// ============== PASSWORD GATE ==============
function PasswordGate({ onAuth }) {
  const [pw, setPw] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErr("");
    try {
      await axios.post(`${API}/admin/verify`, { password: pw });
      sessionStorage.setItem(AUTH_KEY, "1");
      onAuth();
    } catch {
      setErr("Wrong password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="admin-gate" className="min-h-screen bg-[#050505] text-white flex items-center justify-center px-6">
      <form onSubmit={submit} className="w-full max-w-sm bg-[#0e0e0e] border border-white/10 p-8">
        <Lock size={24} className="text-[#D4AF37] mb-4" />
        <h1 className="font-display text-2xl font-bold mb-1">Admin Access</h1>
        <p className="text-sm text-white/50 mb-6">Tekreck · /ygtoadmin155</p>
        <input
          data-testid="admin-password-input"
          type="password"
          value={pw}
          onChange={(e) => setPw(e.target.value)}
          placeholder="Password"
          autoFocus
          className="w-full bg-black border border-white/15 text-white px-4 py-3 outline-none focus:border-[#D4AF37]"
        />
        {err && (
          <div data-testid="admin-password-error" className="text-red-400 text-xs mt-2">
            {err}
          </div>
        )}
        <button
          data-testid="admin-password-submit"
          type="submit"
          disabled={loading}
          className="mt-5 w-full h-11 bg-[#D4AF37] text-black text-xs uppercase tracking-[0.2em] font-semibold hover:bg-[#F1C40F] disabled:opacity-60"
        >
          {loading ? "Checking…" : "Unlock"}
        </button>
      </form>
    </div>
  );
}

// ============== ALBUMS TAB ==============
const blankAlbum = () => ({
  title: "",
  year: "",
  era: "",
  cover: "",
  bandlab_url: "",
  embed_url: "",
  tracks: [],
  order: 0,
});

function AlbumsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/albums`);
      setItems(data);
    } catch {
      toast.error("Couldn't load albums.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const saveAlbum = async (album) => {
    try {
      const payload = {
        title: album.title,
        year: album.year,
        era: album.era,
        cover: album.cover,
        bandlab_url: album.bandlab_url,
        embed_url: album.embed_url,
        tracks: album.tracks,
        order: Number(album.order) || 0,
      };
      if (album.id) await axios.put(`${API}/albums/${album.id}`, payload);
      else await axios.post(`${API}/albums`, payload);
      toast.success(album.id ? "Album updated." : "Album added.");
      setEditing(null);
      load();
    } catch {
      toast.error("Save failed.");
    }
  };

  const deleteAlbum = async (id) => {
    if (!window.confirm("Delete this album?")) return;
    try {
      await axios.delete(`${API}/albums/${id}`);
      toast.success("Deleted.");
      load();
    } catch {
      toast.error("Delete failed.");
    }
  };

  return (
    <div data-testid="admin-albums-tab">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">
          Albums ({items.length})
        </h1>
        <div className="flex gap-3">
          <button data-testid="admin-refresh-albums" onClick={load} className="inline-flex items-center gap-2 h-10 px-4 border border-white/15 text-xs uppercase tracking-[0.2em] hover:border-[#D4AF37]">
            <RefreshCw size={14} /> Refresh
          </button>
          <button data-testid="admin-add-album" onClick={() => setEditing(blankAlbum())} className="inline-flex items-center gap-2 h-10 px-5 bg-[#D4AF37] text-black text-xs uppercase tracking-[0.2em] font-semibold hover:bg-[#F1C40F]">
            <Plus size={14} /> Add Album
          </button>
        </div>
      </div>

      {loading ? <p className="text-white/50">Loading…</p> : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((a) => (
            <div key={a.id} data-testid={`admin-album-row-${a.id}`} className="bg-[#0e0e0e] border border-white/10 p-4 flex gap-4">
              {a.cover ? (
                <img src={a.cover} alt="" className="w-20 h-20 object-cover border border-white/10 flex-shrink-0" />
              ) : (
                <div className="w-20 h-20 bg-black/60 border border-white/10 flex items-center justify-center text-white/30">
                  <Disc3 size={20} />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="font-display text-base font-semibold truncate">{a.title}</div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 mt-1">{a.year} · {a.era} · order {a.order}</div>
                <div className="text-xs text-white/50 mt-1">{a.tracks?.length || 0} tracks {a.embed_url ? "· 🎵 embed" : ""}</div>
                <div className="flex gap-2 mt-3">
                  <button data-testid={`admin-edit-album-${a.id}`} onClick={() => setEditing(a)} className="h-8 px-3 border border-white/15 text-[10px] uppercase tracking-[0.2em] hover:border-[#D4AF37]">Edit</button>
                  <button data-testid={`admin-delete-album-${a.id}`} onClick={() => deleteAlbum(a.id)} className="h-8 px-3 border border-red-500/30 text-red-400 text-[10px] uppercase tracking-[0.2em] hover:bg-red-500/10"><Trash2 size={12} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && <AlbumEditor album={editing} onCancel={() => setEditing(null)} onSave={saveAlbum} />}
    </div>
  );
}

function AlbumEditor({ album, onCancel, onSave }) {
  const [form, setForm] = useState(album);
  const [trackInput, setTrackInput] = useState("");
  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const addTrack = () => {
    const t = trackInput.trim();
    if (!t) return;
    setForm({ ...form, tracks: [...(form.tracks || []), t] });
    setTrackInput("");
  };
  const removeTrack = (i) => setForm({ ...form, tracks: form.tracks.filter((_, idx) => idx !== i) });
  const moveTrack = (i, dir) => {
    const next = [...form.tracks];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    setForm({ ...form, tracks: next });
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-start md:items-center justify-center p-4 overflow-y-auto">
      <div data-testid="admin-album-editor" className="w-full max-w-2xl bg-[#0e0e0e] border border-[#D4AF37]/40 gold-glow p-6 md:p-8 my-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-display text-2xl font-bold">{form.id ? "Edit Album" : "New Album"}</h2>
          <button data-testid="admin-editor-close" onClick={onCancel} className="text-white/50 hover:text-white"><X size={18} /></button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <AdminField label="Title"><input data-testid="editor-title" value={form.title} onChange={update("title")} className="admin-input" /></AdminField>
          <AdminField label="Year"><input data-testid="editor-year" value={form.year} onChange={update("year")} className="admin-input" /></AdminField>
          <AdminField label="Era (Gospel / Collab / Mixtape / Pre-Gospel)"><input data-testid="editor-era" value={form.era} onChange={update("era")} className="admin-input" /></AdminField>
          <AdminField label="Display order"><input data-testid="editor-order" type="number" value={form.order} onChange={update("order")} className="admin-input" /></AdminField>
          <AdminField label="Cover image URL" wide><input data-testid="editor-cover" value={form.cover} onChange={update("cover")} placeholder="https://…" className="admin-input" /></AdminField>
          <AdminField label="BandLab album URL" wide><input data-testid="editor-bandlab" value={form.bandlab_url} onChange={update("bandlab_url")} placeholder="https://www.bandlab.com/tekreck/albums/…" className="admin-input" /></AdminField>
          <AdminField label="BandLab embed — paste the full <iframe> snippet OR just the URL" wide>
            <textarea
              data-testid="editor-embed"
              value={form.embed_url}
              onChange={(e) => setForm({ ...form, embed_url: extractEmbedSrc(e.target.value) })}
              rows={2}
              placeholder='<iframe src="https://www.bandlab.com/embed/collection/?id=…" …></iframe>'
              className="admin-input resize-none font-mono text-xs"
            />
            {form.embed_url && (
              <div className="mt-3 border border-[#D4AF37]/30">
                <iframe
                  title="Embed preview"
                  src={form.embed_url}
                  width="100%"
                  height="200"
                  frameBorder="0"
                  allow="autoplay; encrypted-media"
                  allowFullScreen
                  style={{ display: "block", background: "#000" }}
                />
              </div>
            )}
          </AdminField>
        </div>

        <div className="mt-6">
          <div className="text-[10px] uppercase tracking-[0.3em] text-white/50 mb-2">Tracklist</div>
          <div className="flex gap-2 mb-3">
            <input data-testid="editor-track-input" value={trackInput} onChange={(e) => setTrackInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addTrack())} placeholder="Track name, press Enter or Add" className="admin-input flex-1" />
            <button data-testid="editor-add-track" type="button" onClick={addTrack} className="h-11 px-4 bg-[#D4AF37] text-black text-xs uppercase tracking-[0.2em] font-semibold">Add</button>
          </div>
          <ul className="space-y-1 max-h-60 overflow-y-auto">
            {(form.tracks || []).map((t, i) => (
              <li key={i} data-testid={`editor-track-row-${i}`} className="flex items-center gap-2 px-3 py-2 bg-black/40 border border-white/10">
                <span className="text-[#D4AF37] text-xs w-6 tabular-nums">{String(i + 1).padStart(2, "0")}</span>
                <span className="flex-1 text-sm">{t}</span>
                <button onClick={() => moveTrack(i, -1)} className="text-white/40 hover:text-white text-xs px-1">↑</button>
                <button onClick={() => moveTrack(i, 1)} className="text-white/40 hover:text-white text-xs px-1">↓</button>
                <button onClick={() => removeTrack(i)} className="text-red-400 hover:text-red-300 text-xs px-1">×</button>
              </li>
            ))}
          </ul>
        </div>

        <div className="flex gap-3 mt-8">
          <button data-testid="editor-save" onClick={() => onSave(form)} className="inline-flex items-center gap-2 h-11 px-6 bg-[#D4AF37] text-black text-xs uppercase tracking-[0.2em] font-semibold hover:bg-[#F1C40F]">
            <Save size={14} /> Save
          </button>
          <button data-testid="editor-cancel" onClick={onCancel} className="h-11 px-6 border border-white/15 text-xs uppercase tracking-[0.2em]">Cancel</button>
        </div>

        <style>{`
          .admin-input { width: 100%; background: #050505; border: 1px solid rgba(255,255,255,0.12); color: #fff; padding: 12px 14px; font-size: 14px; outline: none; transition: border-color 0.2s; }
          .admin-input:focus { border-color: #D4AF37; }
          .admin-input::placeholder { color: rgba(255,255,255,0.3); }
        `}</style>
      </div>
    </div>
  );
}

function AdminField({ label, wide, children }) {
  return (
    <label className={`block ${wide ? "md:col-span-2" : ""}`}>
      <span className="block text-[10px] uppercase tracking-[0.3em] text-white/50 mb-2">{label}</span>
      {children}
    </label>
  );
}

// ============== RECENT TRACKS TAB ==============
const blankTrack = () => ({ title: "", album: "", length: "", url: "", embed_url: "", order: 0 });

function RecentTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/recent-tracks`);
      setItems(data);
    } catch {
      toast.error("Couldn't load tracks.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const save = async (t) => {
    try {
      const payload = {
        title: t.title,
        album: t.album,
        length: t.length,
        url: t.url,
        embed_url: t.embed_url,
        order: Number(t.order) || 0,
      };
      if (t.id) await axios.put(`${API}/recent-tracks/${t.id}`, payload);
      else await axios.post(`${API}/recent-tracks`, payload);
      toast.success("Saved.");
      setEditing(null);
      load();
    } catch {
      toast.error("Save failed.");
    }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this track?")) return;
    try {
      await axios.delete(`${API}/recent-tracks/${id}`);
      toast.success("Deleted.");
      load();
    } catch {
      toast.error("Delete failed.");
    }
  };

  return (
    <div data-testid="admin-recent-tab">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Recent Tracks ({items.length})</h1>
        <div className="flex gap-3">
          <button data-testid="admin-refresh-recent" onClick={load} className="inline-flex items-center gap-2 h-10 px-4 border border-white/15 text-xs uppercase tracking-[0.2em] hover:border-[#D4AF37]"><RefreshCw size={14} /> Refresh</button>
          <button data-testid="admin-add-track" onClick={() => setEditing(blankTrack())} className="inline-flex items-center gap-2 h-10 px-5 bg-[#D4AF37] text-black text-xs uppercase tracking-[0.2em] font-semibold hover:bg-[#F1C40F]"><Plus size={14} /> Add Track</button>
        </div>
      </div>

      {loading ? <p className="text-white/50">Loading…</p> : (
        <div className="space-y-2">
          {items.map((t) => (
            <div key={t.id} data-testid={`admin-recent-row-${t.id}`} className="bg-[#0e0e0e] border border-white/10 p-4 flex flex-wrap items-center gap-4">
              <span className="font-display text-[#D4AF37] text-sm w-8 tabular-nums">{String(t.order).padStart(2, "0")}</span>
              <div className="flex-1 min-w-[200px]">
                <div className="font-display text-base font-semibold">{t.title}</div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 mt-1">{t.album} {t.length && `· ${t.length}`} {t.embed_url ? "· 🎵 embed" : ""}</div>
                {t.url && <a href={t.url} target="_blank" rel="noreferrer" className="text-xs text-[#D4AF37]/80 hover:text-[#D4AF37] truncate block max-w-md mt-1">{t.url}</a>}
              </div>
              <div className="flex gap-2">
                <button data-testid={`admin-edit-track-${t.id}`} onClick={() => setEditing(t)} className="h-8 px-3 border border-white/15 text-[10px] uppercase tracking-[0.2em] hover:border-[#D4AF37]">Edit</button>
                <button data-testid={`admin-delete-track-${t.id}`} onClick={() => remove(t.id)} className="h-8 px-3 border border-red-500/30 text-red-400 text-[10px] uppercase tracking-[0.2em] hover:bg-red-500/10"><Trash2 size={12} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && <TrackEditor track={editing} onCancel={() => setEditing(null)} onSave={save} />}
    </div>
  );
}

function TrackEditor({ track, onCancel, onSave }) {
  const [form, setForm] = useState(track);
  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
      <div data-testid="admin-track-editor" className="w-full max-w-xl bg-[#0e0e0e] border border-[#D4AF37]/40 gold-glow p-6 md:p-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-display text-2xl font-bold">{form.id ? "Edit Track" : "New Track"}</h2>
          <button onClick={onCancel} className="text-white/50 hover:text-white"><X size={18} /></button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <AdminField label="Title" wide><input data-testid="track-editor-title" value={form.title} onChange={update("title")} className="admin-input" /></AdminField>
          <AdminField label="Album / Single"><input data-testid="track-editor-album" value={form.album} onChange={update("album")} className="admin-input" /></AdminField>
          <AdminField label="Length (3:42)"><input data-testid="track-editor-length" value={form.length} onChange={update("length")} className="admin-input" /></AdminField>
          <AdminField label="URL (BandLab / Spotify / YouTube)" wide><input data-testid="track-editor-url" value={form.url} onChange={update("url")} className="admin-input" /></AdminField>
          <AdminField label="BandLab embed — paste the full <iframe> snippet OR just the URL" wide>
            <textarea
              data-testid="track-editor-embed"
              value={form.embed_url}
              onChange={(e) => setForm({ ...form, embed_url: extractEmbedSrc(e.target.value) })}
              rows={2}
              placeholder='<iframe src="https://www.bandlab.com/embed/?id=…" …></iframe>'
              className="admin-input resize-none font-mono text-xs"
            />
            {form.embed_url && (
              <div className="mt-3 border border-[#D4AF37]/30">
                <iframe
                  title="Embed preview"
                  src={form.embed_url}
                  width="100%"
                  height="160"
                  frameBorder="0"
                  allow="autoplay; encrypted-media"
                  allowFullScreen
                  style={{ display: "block", background: "#000" }}
                />
              </div>
            )}
          </AdminField>
          <AdminField label="Order"><input data-testid="track-editor-order" type="number" value={form.order} onChange={update("order")} className="admin-input" /></AdminField>
        </div>
        <div className="flex gap-3 mt-8">
          <button data-testid="track-editor-save" onClick={() => onSave(form)} className="inline-flex items-center gap-2 h-11 px-6 bg-[#D4AF37] text-black text-xs uppercase tracking-[0.2em] font-semibold hover:bg-[#F1C40F]"><Save size={14} /> Save</button>
          <button onClick={onCancel} className="h-11 px-6 border border-white/15 text-xs uppercase tracking-[0.2em]">Cancel</button>
        </div>
        <style>{`
          .admin-input { width: 100%; background: #050505; border: 1px solid rgba(255,255,255,0.12); color: #fff; padding: 12px 14px; font-size: 14px; outline: none; }
          .admin-input:focus { border-color: #D4AF37; }
        `}</style>
      </div>
    </div>
  );
}

// ============== READ-ONLY LISTS ==============
function ReadOnlyList({ endpoint, title, testIdPrefix, kind }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/${endpoint}`);
      setItems(data);
    } catch {
      toast.error("Couldn't load.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [endpoint]);

  return (
    <div data-testid={`admin-${endpoint}-list`}>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">{title} ({items.length})</h1>
        <button onClick={load} data-testid={`admin-refresh-${endpoint}`} className="inline-flex items-center gap-2 h-10 px-4 border border-white/15 text-xs uppercase tracking-[0.2em] hover:border-[#D4AF37]"><RefreshCw size={14} /> Refresh</button>
      </div>
      {loading ? <p className="text-white/50">Loading…</p>
        : items.length === 0 ? <p className="text-white/50">No entries yet.</p>
        : (
          <div className="space-y-3">
            {items.map((it) => (
              <div key={it.id} data-testid={`admin-${testIdPrefix}-entry-${it.id}`} className="bg-[#0e0e0e] border border-white/10 p-5">
                <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
                  <div className="font-display text-lg font-semibold">{it.name}</div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-white/40">{new Date(it.created_at).toLocaleString()}</div>
                </div>
                <div className="text-sm text-white/60 space-y-1">
                  {kind === "collab" && (
                    <>
                      <div><span className="text-white/40">Email:</span> {it.email}</div>
                      {it.role && <div><span className="text-white/40">Role:</span> {it.role}</div>}
                      {it.social_link && <div><span className="text-white/40">Social:</span> <a href={it.social_link} target="_blank" rel="noreferrer" className="text-[#D4AF37]">{it.social_link}</a></div>}
                      <div className="pt-2 text-white/80 whitespace-pre-wrap">{it.message}</div>
                    </>
                  )}
                  {kind === "booking" && (
                    <>
                      <div><span className="text-white/40">Email:</span> {it.email}</div>
                      {it.event_date && <div><span className="text-white/40">Date:</span> {it.event_date}</div>}
                      {it.venue && <div><span className="text-white/40">Venue:</span> {it.venue}</div>}
                      {it.location && <div><span className="text-white/40">Location:</span> {it.location}</div>}
                      {it.budget_range && <div><span className="text-white/40">Budget:</span> {it.budget_range}</div>}
                      <div className="pt-2 text-white/80 whitespace-pre-wrap">{it.message}</div>
                    </>
                  )}
                  {kind === "prayer" && (
                    <>
                      <div><span className="text-white/40">Contact:</span> {it.contact}</div>
                      <div><span className="text-white/40">Wants callback:</span> {it.wants_callback ? "Yes" : "No"}</div>
                      <div className="pt-2 text-white/80 whitespace-pre-wrap">{it.prayer_need}</div>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
    </div>
  );
}
